/**
 * COIN MARKETPLACE
 * 
 * Componente para comprar coins
 * - Mostrar paquetes disponibles
 * - Comparador de precios
 * - Redirigir a Mercado Pago
 */

import React, { useState, useEffect } from 'react'
import { ShoppingCart, Zap, TrendingUp, AlertCircle, Loader, X } from 'lucide-react'

const DEFAULT_PACKAGES = [
  { id: 'package_1', name: 'Basico', description: 'Ideal para empezar', coins: 10, price_ars: 28000, price_usd: 25, discount_percent: 0 },
  { id: 'package_2', name: 'Profesional', description: 'El mas elegido por peritos', coins: 25, price_ars: 63000, price_usd: 56, discount_percent: 10 },
  { id: 'package_3', name: 'Estudio', description: 'Para volumen mensual alto', coins: 60, price_ars: 145000, price_usd: 129, discount_percent: 14 },
  { id: 'package_4', name: 'Enterprise', description: 'Maximo rendimiento por coin', coins: 120, price_ars: 276000, price_usd: 246, discount_percent: 18 },
]

export default function CoinMarketplace({ userRef: userRefProp, onPurchaseSuccess }) {
  // Obtener userRef de props o localStorage o URL
  const [resolvedUserRef, setResolvedUserRef] = useState(() => {
    if (userRefProp) return userRefProp
    const urlParams = new URLSearchParams(window.location.search)
    const refFromUrl = urlParams.get('user') || urlParams.get('cuit')
    if (refFromUrl) return refFromUrl
    return localStorage.getItem('userRef')
  })

  const [packages, setPackages] = useState([])
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [inflationInfo, setInflationInfo] = useState(null)
  const [purchasePending, setPurchasePending] = useState(false)
  const [pendingMessage, setPendingMessage] = useState('')

  const fetchCurrentBalance = async () => {
    if (!resolvedUserRef) return null
    const token = localStorage.getItem('userToken')
    const res = await fetch('/api/coins/balance', {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
    const data = await res.json()
    if (!res.ok || !data?.success) return null
    return Number(data.coins_balance || 0)
  }

  const startBalanceWatcher = async (baseBalance, timeoutMs = 180000) => {
    const startedAt = Date.now()
    setPurchasePending(true)
    setPendingMessage('Esperando confirmacion de pago y acreditacion de coins...')
    while (Date.now() - startedAt < timeoutMs) {
      await new Promise((r) => setTimeout(r, 4000))
      const next = await fetchCurrentBalance().catch(() => null)
      if (next !== null && next > baseBalance) {
        setPurchasePending(false)
        setPendingMessage('Pago confirmado. Coins acreditados.')
        if (onPurchaseSuccess) onPurchaseSuccess(next)
        setTimeout(() => {
          window.location.href = '/'
        }, 1200)
        return
      }
    }
    setPurchasePending(false)
    setPendingMessage('El pago puede estar pendiente. Actualiza el saldo en unos minutos.')
  }

  // Cargar precios
  useEffect(() => {
    fetchPricing()
  }, [])

  const fetchPricing = async () => {
    setPackages(DEFAULT_PACKAGES)
    try {
      const token = localStorage.getItem('userToken')
      const response = await fetch('/api/coins/pricing', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      if (!response.ok) return
      const data = await response.json()

      if (data.success) {
        const packagesArray = Object.entries(data.packages).map(([id, pkg]) => ({
          id,
          ...pkg
        }))
        setPackages(packagesArray)
        setInflationInfo(data.inflation_info)
      }
    } catch (err) {
      console.warn('Pricing API no disponible, usando precios base')
    }
  }

  const handlePurchase = async (pkg) => {
    if (!resolvedUserRef) {
      setError('Usuario no autenticado')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('userToken')
      let response = await fetch('/api/coins/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          userRef: resolvedUserRef,
          package_id: pkg.id
        })
      })

      const data = await response.json()

      if (!data.success) {
        setError(data.error || 'Error creando pago')
        return
      }

      // Redirigir a Mercado Pago
      const initPoint = data.init_point || data.initPoint
      if (initPoint) {
        const baseBalance = (await fetchCurrentBalance()) ?? 0
        window.open(initPoint, '_blank', 'noopener,noreferrer')
        startBalanceWatcher(baseBalance).catch(() => {
          setPurchasePending(false)
          setPendingMessage('No se pudo verificar saldo automaticamente. Revisa en unos minutos.')
        })
      } else {
        setError('Pasarela de pago no disponible temporalmente. Intenta mas tarde.')
      }
    } catch (err) {
      console.error('Error:', err)
      setError('Error creando preferencia de pago')
    } finally {
      setIsLoading(false)
    }
  }

  // Si no hay userRef, mostrar error
  if (!resolvedUserRef) {
    return (
      <div className="min-h-screen bg-transparent px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="panel-profile rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.18)] backdrop-blur-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h2 className="text-profile-danger text-2xl font-bold">Usuario No Encontrado</h2>
            </div>
            <p className="text-profile-secondary mb-6">
              Debes estar logueado para comprar coins. Por favor:
            </p>
            <ol className="text-profile-secondary mb-6 list-inside list-decimal space-y-2">
              <li>Regresa a la calculadora</li>
              <li>Ingresa tu CUIT/DNI</li>
              <li>Haz clic en "Comprar Coins"</li>
            </ol>
            <a
              href="/"
              className="btn-profile-solid inline-block rounded-xl px-6 py-2 font-bold transition"
            >
              Volver a Inicio
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <a
          href="/"
          aria-label="Volver al inicio"
          className="group fixed right-5 top-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-slate-900/85 text-white shadow-[0_12px_36px_rgba(15,23,42,0.35)] transition hover:scale-105"
        >
          <span className="absolute inset-0 rounded-full ring-2 ring-white/25 ring-offset-2 ring-offset-transparent" />
          <X className="relative h-5 w-5" />
        </a>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Zap className="w-8 h-8 text-yellow-500" />
            <h1 className="text-4xl font-bold text-profile-primary">Compra Coins</h1>
          </div>
          <p className="text-profile-secondary mb-2 text-lg">
            Acceso ilimitado a cálculos previsionales profesionales
          </p>
          {inflationInfo && (
            <p className="text-profile-ok flex items-center justify-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4" />
              Precios actualizados por IPC: {inflationInfo.last_inflation_rate}%
            </p>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="panel-profile-danger mb-8 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-profile-danger flex-shrink-0 mt-0.5" />
            <div>
                <h3 className="font-semibold text-profile-danger">Error</h3>
                <p className="text-sm text-profile-danger">{error}</p>
            </div>
          </div>
        )}

        {pendingMessage && (
          <div className={`mb-8 rounded-xl p-4 flex items-start gap-3 ${purchasePending ? 'panel-profile-warn' : 'panel-profile-ok'}`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${purchasePending ? 'text-profile-warn' : 'text-profile-ok'}`} />
            <div>
              <h3 className={`font-semibold ${purchasePending ? 'text-profile-warn' : 'text-profile-ok'}`}>{purchasePending ? 'Compra en curso' : 'Estado de compra'}</h3>
              <p className={`text-sm ${purchasePending ? 'text-profile-warn' : 'text-profile-ok'}`}>{pendingMessage}</p>
            </div>
          </div>
        )}

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`panel-profile relative rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.16)] backdrop-blur-2xl transition-all duration-300 ${
                pkg.discount_percent > 0
                  ? 'ring-2 ring-amber-400/70'
                  : 'hover:border-slate-300/50'
              }`}
            >
              {/* Badge Recomendado */}
              {pkg.id === 'package_2' && (
                <div className="panel-profile-warn absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold text-profile-warn">
                  ⭐ Recomendado
                </div>
              )}

              {/* Descuento Badge */}
              {pkg.discount_percent > 0 && (
                <div className="panel-profile-danger absolute -top-3 -right-3 rounded-full w-12 h-12 flex items-center justify-center font-bold text-sm text-profile-danger">
                  -{pkg.discount_percent}%
                </div>
              )}

              <div className="p-8">
                {/* Nombre */}
                  <h3 className="text-profile-primary mb-2 text-2xl font-bold">{pkg.name}</h3>

                {/* Descripción */}
                  <p className="text-profile-secondary mb-6 text-sm">{pkg.description}</p>

                {/* Coins */}
                <div className="mb-6">
                  <div className="text-4xl font-bold text-profile-accent mb-1">
                    {pkg.coins}
                  </div>
                    <p className="text-profile-secondary text-sm">coins</p>
                </div>

                {/* Precio ARS */}
                  <div className="panel-profile-accent mb-4 rounded-xl p-4">
                    <p className="text-profile-secondary text-sm">Precio en ARS</p>
                    <p className="text-profile-accent text-3xl font-bold">
                    ${pkg.price_ars.toLocaleString('es-AR')}
                  </p>
                </div>

                {/* Precio USD */}
                  <div className="panel-profile-ok mb-6 rounded-xl p-4">
                    <p className="text-profile-secondary text-sm">En USD (aprox.)</p>
                    <p className="text-profile-ok text-2xl font-bold">${pkg.price_usd}</p>
                </div>

                {/* Costo por coin */}
                  <div className="text-profile-secondary mb-6 text-center text-sm">
                  <p>${Math.round(pkg.price_ars / pkg.coins).toLocaleString('es-AR')} por coin</p>
                </div>

                {/* Botón */}
                <button
                  onClick={() => handlePurchase(pkg)}
                  disabled={isLoading || !resolvedUserRef}
                    className={`w-full py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2 ${
                      pkg.id === 'package_2'
                        ? 'btn-profile-gold disabled:opacity-50'
                        : 'btn-profile-solid disabled:opacity-50'
                    }`}
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Comprar
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="panel-profile rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.16)] backdrop-blur-2xl">
            <h2 className="text-profile-primary mb-6 text-2xl font-bold">Informacion importante</h2>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="btn-profile-solid flex h-8 w-8 items-center justify-center rounded-md">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-profile-primary font-semibold">Coins Sin Expiracion</h3>
                  <p className="text-profile-secondary text-sm">
                    Tus coins nunca expiran mientras hagas cálculos. Después de 180 días sin usar se
                    marcan como expirados.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="btn-profile-solid flex h-8 w-8 items-center justify-center rounded-md">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-profile-primary font-semibold">1 Calculo Gratis/Mes</h3>
                  <p className="text-profile-secondary text-sm">
                    Además de los coins, todos los usuarios obtienen 1 cálculo completamente gratis cada mes (se
                    resetea el 1ro).
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="btn-profile-solid flex h-8 w-8 items-center justify-center rounded-md">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-profile-primary font-semibold">Precios Actualizados por Inflacion</h3>
                  <p className="text-profile-secondary text-sm">
                    Los precios se actualizan automáticamente cada mes según la inflación INDEC. Tus coins no
                    pierden valor.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="btn-profile-solid flex h-8 w-8 items-center justify-center rounded-md">
                    ✓
                  </div>
                </div>
                <div>
                  <h3 className="text-profile-primary font-semibold">Pago Seguro</h3>
                  <p className="text-profile-secondary text-sm">
                    Todos los pagos se procesan a través de Mercado Pago con máxima seguridad.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-profile rounded-3xl p-8 backdrop-blur-2xl">
            <h2 className="text-profile-primary mb-6 text-2xl font-bold">Preguntas Frecuentes</h2>

          <div className="space-y-6">
            <div>
               <h3 className="text-profile-primary mb-2 font-semibold">¿Cuánto cuesta cada cálculo?</h3>
               <p className="text-profile-secondary text-sm">
                Los cálculos cuestan 2-3 coins. Un cálculo simple (solo ANSES) cuesta 2 coins, mientras que un
                cálculo completo (multi-caja) cuesta 3 coins.
              </p>
            </div>

            <div>
               <h3 className="text-profile-primary mb-2 font-semibold">¿Puedo cambiar la cotización USD/ARS?</h3>
               <p className="text-profile-secondary text-sm">
                No, los precios en ARS se actualizan automáticamente cada mes según la inflación INDEC oficial.
              </p>
            </div>

            <div>
               <h3 className="text-profile-primary mb-2 font-semibold">¿Qué pasa si mis coins expiran?</h3>
               <p className="text-profile-secondary text-sm">
                Los coins solo expiran si no hay actividad por 180 días corridos. Si usás la plataforma con
                regularidad, se mantienen activos.
              </p>
            </div>

            <div>
               <h3 className="text-profile-primary mb-2 font-semibold">¿Hay reembolsos?</h3>
               <p className="text-profile-secondary text-sm">
                No. Los coins comprados no se reembolsan. El valor por coin depende del paquete elegido y puede
                 variar por actualización de precios.
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}
